import React, { useState, useRef, useEffect } from 'react';
import { Globe, Check, ChevronDown } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Default export — backward-compatible pill switcher.
 * Reads supported languages dynamically (now: EN / FR / DE / IT).
 * Variants:
 *   - 'pill'    (default) — rounded with globe icon
 *   - 'minimal'           — flat code-only buttons
 */
const LanguageSwitcher = ({ variant = 'pill', className = '' }) => {
  const { currentLanguage, changeLanguage, getAvailableLanguages } = useLanguage();
  const options = getAvailableLanguages().map(({ code }) => ({ code, label: code.toUpperCase() }));

  if (variant === 'minimal') {
    return (
      <div className={className} role="radiogroup" aria-label="Language" data-testid="language-switcher">
        {options.map((o) => (
          <button
            key={o.code}
            type="button"
            role="radio"
            aria-checked={currentLanguage === o.code}
            data-testid={`lang-${o.code}`}
            onClick={() => changeLanguage(o.code)}
            style={{
              minWidth: 38,
              minHeight: 32,
              padding: '4px 8px',
              borderRadius: 6,
              border: 'none',
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: '0.04em',
              background: currentLanguage === o.code ? 'rgba(255,255,255,0.1)' : 'transparent',
              color: currentLanguage === o.code ? '#EDEDED' : '#6B6B6B',
              cursor: 'pointer',
              touchAction: 'manipulation',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            {o.label}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div
      role="radiogroup"
      aria-label="Language"
      data-testid="language-switcher"
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: 4,
        borderRadius: 999,
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
        minHeight: 44,
      }}
    >
      <Globe size={14} style={{ color: '#A1A1A1', marginLeft: 8 }} aria-hidden="true" />
      {options.map((o) => {
        const active = currentLanguage === o.code;
        return (
          <button
            key={o.code}
            type="button"
            role="radio"
            aria-checked={active}
            data-testid={`lang-${o.code}`}
            onClick={() => changeLanguage(o.code)}
            style={{
              minWidth: 40,
              minHeight: 32,
              padding: '6px 10px',
              borderRadius: 999,
              border: 'none',
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: '0.05em',
              background: active ? '#FFFFFF' : 'transparent',
              color: active ? '#0A0A0D' : '#A1A1A1',
              cursor: 'pointer',
              transition: 'background 160ms ease, color 160ms ease',
              touchAction: 'manipulation',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
};

/**
 * Named export — compact dropdown trigger for the landing nav.
 * Shows current 2-letter code; opens a popover with all supported languages.
 * Styles come from `revenue-landing.css` (.rl-lang-*).
 */
export const LanguageDropdown = () => {
  const { currentLanguage, changeLanguage, getAvailableLanguages } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const langs = getAvailableLanguages();

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  return (
    <div className="rl-lang-switcher" ref={ref} data-testid="lang-dropdown">
      <button
        type="button"
        className="rl-lang-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="lang-dropdown-trigger"
      >
        <Globe size={14} />
        <span>{currentLanguage}</span>
        <ChevronDown size={14} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
      </button>
      {open && (
        <div className="rl-lang-menu" role="listbox">
          {langs.map((l) => (
            <button
              key={l.code}
              type="button"
              className={l.code === currentLanguage ? 'active' : ''}
              onClick={() => { changeLanguage(l.code); setOpen(false); }}
              role="option"
              aria-selected={l.code === currentLanguage}
              data-testid={`lang-option-${l.code}`}
            >
              <span style={{ fontSize: 18 }} aria-hidden="true">{l.flag}</span>
              <span style={{ flex: 1 }}>{l.nativeName}</span>
              {l.code === currentLanguage && <Check size={14} color="#60A5FA" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Named export — 2-column tile grid for the mobile drawer.
 */
export const LanguageGrid = ({ onPick }) => {
  const { currentLanguage, changeLanguage, getAvailableLanguages } = useLanguage();
  const langs = getAvailableLanguages();
  const pick = (code) => {
    changeLanguage(code);
    if (onPick) onPick(code);
  };
  return (
    <div className="rl-mobile-lang-grid" data-testid="lang-grid">
      {langs.map((l) => (
        <button
          key={l.code}
          type="button"
          className={l.code === currentLanguage ? 'active' : ''}
          onClick={() => pick(l.code)}
          aria-pressed={l.code === currentLanguage}
          data-testid={`lang-grid-${l.code}`}
        >
          <span style={{ fontSize: 18 }} aria-hidden="true">{l.flag}</span>
          <span>{l.nativeName}</span>
        </button>
      ))}
    </div>
  );
};

export default LanguageSwitcher;
